import { Component, OnInit } from '@angular/core';
import { DataService } from 'src/app/services/data.service';
import { SecurityService, WorkflowContextService } from 'src/app/services';
import { Action, Role } from 'src/app/models';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

interface JanusRole extends Partial<Role> {
  permissions: string[];
  members: {
    id: string;
    name: string;
    email: string;
  }[];
}

@Component({
  selector: 'wm-users-roles',
  standalone: false,
  templateUrl: './users-roles-janus.component.html',
  styleUrls: ['./users-roles-janus.component.scss']
})
export class UsersRolesJanusComponent implements OnInit {
  showAddRoleCard = false;
  roles: Partial<Role>[] = [];
  selectedRole: JanusRole | null = null;
  allPermissions: Partial<Action>[] = [];

  loading = true;
  error = false;

  constructor(
    private dataSvc: DataService,
    private context: WorkflowContextService,
    private securitySvc: SecurityService,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    // Handle roleId (details vs list)
    this.route.paramMap.subscribe(params => {
      const roleId = params.get('roleId');

      if (roleId) {
        this.openRoleDetails({ id: roleId });
      } else {
        this.selectedRole = null;
        this.loadRoles();
      }
    });

    // Handle add route
    this.route.url.subscribe(segments => {
      this.showAddRoleCard = segments.some(s => s.path === 'add');
    });
  }

  loadRoles() {
    this.loading = true;
    this.error = false;

    this.dataSvc.searchRoles(this.context.client, null).subscribe({
      next: (res: Role[]) => {
        this.roles = res;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.error = true;
      }
    });
  }

  openRoleDetails(role: Partial<Role>) {
    if (!role?.id) return;

    this.loading = true;
    this.selectedRole = null;

    this.securitySvc.getRole(role.id).subscribe({
      next: fullRole => {
        const assignedPermissions = (fullRole.actions || []).map(a => a.action);

        this.securitySvc.getAvailableActions(fullRole).subscribe({
          next: availableActions => {
            this.allPermissions = [
              ...availableActions,
              ...assignedPermissions
            ].sort((a, b) => a.name.localeCompare(b.name));

            this.selectedRole = {
              ...fullRole,
              color: fullRole.color ?? null,
              permissions: assignedPermissions.map(a => a.id),
              members: (fullRole.members || []).map(m => ({
                id: m.user.id,
                name: m.user.name,
                email: m.user.userName ?? ''
              }))
            };

            this.loading = false;
          },
          error: () => {
            this.loading = false;
          }
        });
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  deleteRole(): void {
    if (!this.selectedRole?.id) {
      return;
    }

    // Added by: Amisha
    // Date: 2026-08-18
    // Reason: Remove circular role references so the delete API receives the expected payload.
    (this.selectedRole.actions || []).forEach(
      permission => delete permission.role
    );

    this.securitySvc.deleteRole(this.selectedRole).subscribe({
      next: () => {
        this.loadRoles(); // refresh list
        this.selectedRole = null; // close details
      },
      error: () => {
        // optional toast
        console.error('Failed to delete role');
      }
    });
  }
  goToEdit(role: Partial<Role>) {
    if (!role?.id) {
      return;
    }

    if (this.route.snapshot.url.some(s => s.path === 'edit')) {
      return; // already in edit
    }

    this.router.navigate(['../edit', role.id], { relativeTo: this.route });
  }

  removeUser(userId: string) {
    if (!this.selectedRole?.id) {
      return;
    }
    this.securitySvc.removeUserRole(userId, this.selectedRole.id).subscribe({
      next: () => {
        this.selectedRole.members = this.selectedRole.members.filter(
          m => m.id !== userId
        );
      }
    });
  }

  saveRole() {
    if (!this.selectedRole?.id) {
      return;
    }

    // ✅ Build Permission payload EXACTLY like legacy screen
    const permissionsPayload = this.selectedRole.permissions.map(actionId => ({
      id: null, // REQUIRED by backend
      actionId: actionId, // REQUIRED
      roleID: this.selectedRole!.id // REQUIRED
    }));

    const payload = {
      id: this.selectedRole.id,
      name: this.selectedRole.name,
      color: this.selectedRole.color,
      client: this.context.client,
      actions: permissionsPayload
    };

    this.securitySvc.saveRole(payload).subscribe({
      next: () => {
        this.loadRoles();
        this.selectedRole = null;
        this.router.navigate(['../../list'], { relativeTo: this.route });
      },
      error: err => {
        console.error('Save role failed', err);
      }
    });
  }

  openAddRole() {
    this.router.navigate(['../add'], { relativeTo: this.route });
  }

  closeAddRole() {
    this.router.navigate(['../list'], { relativeTo: this.route });
  }

  onRoleCreated() {
    this.loadRoles();
    this.router.navigate(['../list'], { relativeTo: this.route });
  }

  saveNewRole(role: Partial<Role>) {
    this.roles.unshift(role);
    this.router.navigate(['../list'], { relativeTo: this.route });
  }
  closeRoleDetails() {
    this.selectedRole = null;
    this.router.navigate(['../../list'], { relativeTo: this.route });
  }
}
